import { useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { ClockIcon, MailIcon, PencilIcon, TrashIcon } from '@heroicons/react/outline'
import { UserContext } from '../../../hooks/UserContext'
import { Button, ConfirmDialog, Notification, PageTopBar, PageView } from '../../../components'
import { Loader } from '../../../components/Loader'
import {
  createDispatchDocumentRecipient,
  deleteDispatchDocumentRecipient,
  fetchDispatchDocumentRecipients,
  updateDispatchDocumentRecipient,
} from '../../../services'
import { RecipientFormModal } from './RecipientFormModal'
import { RecipientsHistoryModal } from './RecipientsHistoryModal'
import { ALL_CARRIERS, errorKey, groupKey, groupRecipients, isNotFound } from './recipientsConfig'

const T_PREFIX = 'dispatch_recipients'

// Who receives the signed document of each dispatch the carrier takes, per carrier, edited by the
// people who define the carriers' dispatch schedules (same permissions).
export const DispatchDocumentRecipientsContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const { user } = useContext(UserContext)
  const queryClient = useQueryClient()

  const hasAccess = !!user?.permissions?.index_dispatch_schedules

  const [form, setForm] = useState({ open: false, recipient: null, group: ALL_CARRIERS })
  const [toRemove, setToRemove] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [formError, setFormError] = useState(null)
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })

  const queryKey = ['dispatch_document_recipients', warehouse_id]
  const { data, isLoading, isError, refetch } = useQuery(queryKey, () => fetchDispatchDocumentRecipients(warehouse_id), {
    enabled: hasAccess && !!warehouse_id,
    refetchOnWindowFocus: false,
  })
  const groups = useMemo(() => (data ? groupRecipients(data.recipients, data.carriers) : []), [data])
  // What this user may do in this warehouse is the platform's answer (permission and warehouse), not
  // a copy of its rule.
  const canCreate = !!data?.abilities?.create
  const canEdit = !!data?.abilities?.update

  const notify = (type, key) => setNotification({ show: true, type, title: i18n.t(`${T_PREFIX}.${key}`) })
  const refreshList = () => queryClient.invalidateQueries(['dispatch_document_recipients', warehouse_id])
  // Someone else removed it meanwhile: the list on screen is stale, so it is read again.
  const refreshIfGone = error => isNotFound(error) && refreshList()
  const afterWrite = key => {
    refreshList()
    queryClient.invalidateQueries(['dispatch_document_recipients_history', warehouse_id])
    setForm(prev => ({ ...prev, open: false }))
    setFormError(null)
    notify('success', `notifications.${key}`)
  }

  const saveMutation = useMutation(
    ({ id, payload }) => (id ? updateDispatchDocumentRecipient(warehouse_id, id, payload) : createDispatchDocumentRecipient(warehouse_id, payload)),
    {
      onSuccess: (_result, { id }) => afterWrite(id ? 'updated' : 'created'),
      onError: (error, { id }) => {
        // An edit of a recipient someone else removed: the form has nothing left to save.
        if (id && isNotFound(error)) {
          refreshList()
          setForm(prev => ({ ...prev, open: false }))
          setFormError(null)
          notify('error', `errors.${errorKey(error)}`)
          return
        }
        refreshIfGone(error)
        setFormError(error)
      },
    },
  )
  const removeMutation = useMutation(id => deleteDispatchDocumentRecipient(warehouse_id, id), {
    onSuccess: () => afterWrite('removed'),
    onError: error => {
      refreshIfGone(error)
      notify('error', `errors.${errorKey(error)}`)
    },
    onSettled: () => setToRemove(null),
  })

  const openForm = (group, recipient = null) => {
    setFormError(null)
    setForm({ open: true, recipient, group })
  }

  const groupTitle = group => (group.carrierName ? group.carrierName : i18n.t(`${T_PREFIX}.all_carriers`))

  if (!hasAccess) {
    return (
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
          </PageTopBar>
        }
      >
        <div className="text-center py-12 text-gray-500">{i18n.t(`${T_PREFIX}.errors.no_access`)}</div>
      </PageView>
    )
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">{i18n.t(`${T_PREFIX}.title`)}</div>
          <div className="flex items-center gap-2 ml-auto">
            <Button type="secondary" onClick={() => setHistoryOpen(true)}>
              <ClockIcon className="h-4 w-4 mr-1 inline" />
              {i18n.t(`${T_PREFIX}.actions.history`)}
            </Button>
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <Notification
        show={notification.show}
        setShow={show => setNotification(prev => ({ ...prev, show }))}
        type={notification.type}
        title={notification.title}
      />

      <p className="text-sm text-gray-600 mb-4 max-w-3xl">{i18n.t(`${T_PREFIX}.explainer`)}</p>

      {isLoading && <Loader show={true} />}
      {isError && (
        <div className="text-center py-12 text-gray-500">
          {i18n.t(`${T_PREFIX}.errors.load`)}{' '}
          <button type="button" className="text-indigo-600 hover:underline" onClick={() => refetch()}>
            {i18n.t(`${T_PREFIX}.actions.retry`)}
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.map(group => (
            <section
              key={group.key}
              data-testid={`recipient-group-${group.key}`}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900">{groupTitle(group)}</h3>
                <span className="text-xs text-gray-500 whitespace-nowrap">{i18n.t(`${T_PREFIX}.count`, { count: group.recipients.length })}</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">{i18n.t(group.carrierName ? `${T_PREFIX}.carrier_hint` : `${T_PREFIX}.all_carriers_hint`)}</p>

              {group.recipients.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2">{i18n.t(`${T_PREFIX}.empty_group`)}</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {group.recipients.map(recipient => (
                    <li key={recipient.id} className="py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{recipient.name}</div>
                        <div className="text-sm text-gray-600 truncate flex items-center gap-1">
                          <MailIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          {recipient.email}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => openForm(groupKey(recipient.carrier_name), recipient)}
                            className="p-1 text-gray-500 hover:text-indigo-600"
                            title={i18n.t(`${T_PREFIX}.actions.edit`)}
                            aria-label={`${i18n.t(`${T_PREFIX}.actions.edit`)} ${recipient.name}`}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setToRemove(recipient)}
                            className="p-1 text-gray-500 hover:text-red-600"
                            title={i18n.t(`${T_PREFIX}.actions.remove`)}
                            aria-label={`${i18n.t(`${T_PREFIX}.actions.remove`)} ${recipient.name}`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {canCreate && (
                <div className="mt-auto pt-3">
                  <Button type="secondary" className="w-full" onClick={() => openForm(group.key)}>
                    {i18n.t(`${T_PREFIX}.actions.add`)}
                  </Button>
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      <RecipientFormModal
        open={form.open}
        setOpen={open => setForm(prev => ({ ...prev, open }))}
        recipient={form.recipient}
        group={form.group}
        carriers={data?.carriers ?? []}
        onSubmit={payload => saveMutation.mutate({ id: form.recipient?.id, payload })}
        isLoading={saveMutation.isLoading}
        error={formError}
        onClearError={() => setFormError(null)}
      />

      <RecipientsHistoryModal open={historyOpen} setOpen={setHistoryOpen} warehouseId={warehouse_id} />

      <ConfirmDialog
        open={!!toRemove}
        setOpen={open => !open && setToRemove(null)}
        title={i18n.t(`${T_PREFIX}.actions.remove`)}
        description={toRemove ? i18n.t(`${T_PREFIX}.confirm_remove`, { name: toRemove.name, email: toRemove.email }) : ''}
        confirmLabel={i18n.t(`${T_PREFIX}.actions.remove`)}
        cancelLabel={i18n.t(`${T_PREFIX}.form.cancel`)}
        onConfirm={() => removeMutation.mutate(toRemove.id)}
        onCancel={() => setToRemove(null)}
        loading={removeMutation.isLoading}
        type="danger"
      />
    </PageView>
  )
}
